import { Link } from "react-router-dom";
import { useList } from "@/hooks/crud";
import { Spinner, ErrorBanner } from "@/components/Feedback";
import { PIPELINE_STAGES } from "@/lib/enums";
import { formatNumber } from "@/lib/format";
import type { Track } from "@/types/database";

export default function Dashboard() {
  // Pull the whole catalog once (111 rows) to compute the board client-side.
  const { data, isLoading, error } = useList(
    "tracks",
    "select=id,title,pipeline_stage,is_remix,bpm,artist_id&order=updated_at.desc&limit=1000",
  );
  const artists = useList("artists", "select=id,name");
  const releases = useList("track_release", "select=id&status=eq.RELEASED&limit=1");

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBanner error={error} />;

  const tracks = (data?.rows ?? []) as Track[];
  const artistName = new Map(
    (artists.data?.rows ?? []).map((a) => [a.id, a.name]),
  );
  const byStage = new Map<string, Track[]>();
  for (const s of PIPELINE_STAGES) byStage.set(s, []);
  for (const t of tracks) {
    if (!byStage.has(t.pipeline_stage)) byStage.set(t.pipeline_stage, []);
    byStage.get(t.pipeline_stage)!.push(t);
  }

  const remixes = tracks.filter((t) => t.is_remix).length;
  const released = releases.data?.total ?? 0;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <div className="page-sub">
            Ex Pulvis Records — concept → prompt → audio → assets → master → release
          </div>
        </div>
        <Link to="/tracks" className="btn btn-primary">
          Open Tracks
        </Link>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="val">{formatNumber(tracks.length)}</div>
          <div className="lbl">Tracks</div>
        </div>
        <div className="kpi">
          <div className="val">{formatNumber(artists.data?.total ?? 0)}</div>
          <div className="lbl">Artists</div>
        </div>
        <div className="kpi">
          <div className="val">{formatNumber(released)}</div>
          <div className="lbl">Released</div>
        </div>
        <div className="kpi">
          <div className="val">{formatNumber(remixes)}</div>
          <div className="lbl">Remixes</div>
        </div>
      </div>

      <div className="board">
        {PIPELINE_STAGES.map((stage) => {
          const items = byStage.get(stage) ?? [];
          return (
            <div className="board-col" key={stage}>
              <div className="board-col-head">
                <span>{stage}</span>
                <span className="muted mono">{items.length}</span>
              </div>
              <div className="board-col-body">
                {items.map((t) => (
                  <Link key={t.id} to={`/tracks/${t.id}`} className="board-card">
                    <div className="title">{t.title}</div>
                    <div className="meta">
                      {artistName.get(t.artist_id) ?? "—"}
                      {t.bpm ? ` · ${t.bpm} BPM` : ""}
                      {t.is_remix ? " · remix" : ""}
                    </div>
                  </Link>
                ))}
                {items.length === 0 && <div className="muted mono">empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
